import React, { useState, useEffect } from "react";
import { Card, Form, Col, Row, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import authService from "../../../services/authService";
import { useUser } from "../../../context/UserContext";

const GuidedSetupCustNoura = ({ show, handleClose, onSave, initialData }) => {
  const { token } = useUser();
  const [selectedOptions, setSelectedOptions] = useState({
    fullName: true,
    email: false,
    contactNumber: true,
    reasonForCalling: true,
    callingOnBehalf: false,
    whereDidYouHear: true,
    businessName: false,
  });

  const [messageEnabled, setMessageEnabled] = useState(true);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [appointmentType, setAppointmentType] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [calcomApiKey, setCalcomApiKey] = useState("");
  const [messageText, setMessageText] = useState("");
  const [instructions, setInstructions] = useState(
    `- Maintain a friendly, empathetic & gentle tone and use simple, conversational language.
- Do not infer names. Repeat user-provided names exactly as transcribed. If unsure, ask for clarification.
- Keep questions concise and to the point; avoid unnecessary elaboration.
- Add natural fillers like 'uhm', 'hmm', or 'ahan' sparingly for a more human-like experience.
- Avoid interrupting the user and always wait for them to finish speaking.
- Speak at a medium pace and avoid rushing.`
  );
  const [setSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setSelectedOptions({
        fullName: initialData.fullName,
        email: initialData.emailAddress,
        contactNumber: initialData.contactNumber,
        reasonForCalling: initialData.reasonForCalling,
        callingOnBehalf: initialData.callingOnBehalfOf,
        whereDidYouHear: initialData.whereDidYouHearAboutUs,
        businessName: initialData.businessName,
      });
      setMessageEnabled(initialData.takeMessage);
      setScheduleEnabled(!initialData.takeMessage);
      setAppointmentType(
        initialData.isCalcomSelected
          ? "Cal.co"
          : initialData.isZapierSelected
          ? "Zapier"
          : ""
      );
      setAdditionalInfo(
        initialData.isCalcomSelected
          ? initialData.calcomEventTypeId
          : initialData.zapierWebhookUrl
      );
      setCalcomApiKey(initialData.calcomApiKey);
      setMessageText(initialData.message);
      setInstructions(initialData.instructions);
    }
  }, [initialData]);

  const handleCheckboxChange = (e) => {
    setSelectedOptions({
      ...selectedOptions,
      [e.target.name]: e.target.checked,
    });
  };

  const custHandleSaveNoura = async () => {
    setSaving(true);
    const newClientConfig = {
      fullName: selectedOptions.fullName,
      reasonForCalling: selectedOptions.reasonForCalling,
      emailAddress: selectedOptions.email,
      callingOnBehalfOf: selectedOptions.callingOnBehalf,
      contactNumber: selectedOptions.contactNumber,
      whereDidYouHearAboutUs: selectedOptions.whereDidYouHear,
      businessName: selectedOptions.businessName,
      instructions: instructions,
      takeMessage: messageEnabled,
      message: messageText,
      isCalcomSelected: appointmentType === "Cal.co",
      isZapierSelected: appointmentType === "Zapier",
      calcomApiKey: appointmentType === "Cal.co" ? calcomApiKey : "",
      calcomEventTypeId: appointmentType === "Cal.co" ? additionalInfo : "",
      zapierWebhookUrl: appointmentType === "Zapier" ? additionalInfo : "",
    };

    try {
      await authService.updateInstructions(newClientConfig, token);
      onSave(newClientConfig);
      handleClose();
    } catch (error) {
      console.error("Failed to save client configuration:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleScheduleToggle = () => {
    setScheduleEnabled(!scheduleEnabled);
    if (!scheduleEnabled) {
      setMessageEnabled(false);
    }
  };

  const handleMessageToggle = () => {
    setMessageEnabled(!messageEnabled);
    if (!messageEnabled) {
      setScheduleEnabled(false);
    }
  };

  const [selectedTags, setSelectedTags] = useState([
    "Friendly",
    "Empathetic",
    "Kind",
  ]);
  const [addFillers, setAddFillers] = useState(true);

  const behaviorTags = [
    "Friendly",
    "Empathetic",
    "Kind",
    "Professional",
    "Direct",
  ];

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  return (
    <div className="container mt-4">
      <h5 className="fw-bold">Customise Nouraa to manage calls your way.</h5>
      <p>This gives Nouraa the context to handle your calls</p>
      <div className="border rounded">
        <div className="d-flex align-items-center border-bottom p-3">
          <img
            src="/assets/ooui_recent-changes-ltr.svg"
            alt="Right Icon"
            className="me-3"
          />
          <span className="fs-5 fw-bold">Customize Nouraa</span>
        </div>
        {/* Caller Information */}
        <div className="p-3">
          <Card className="p-4 mb-3 border">
            <h6>Caller Information Collection</h6>
            <p className="text-muted">
              What information should we collect from a new client?
            </p>
            <div className="d-flex flex-wrap gap-3">
              <div className="d-flex flex-column flex-grow-1">
                {["Full Name", "Email Address", "Contact Number"].map(
                  (label) => (
                    <Form.Check
                      key={label}
                      type="checkbox"
                      label={label}
                      name={label.toLowerCase().replace(/\s/g, "")}
                      checked={
                        selectedOptions[label.toLowerCase().replace(/\s/g, "")]
                      }
                      onChange={handleCheckboxChange}
                      className="purple-custom-checkbox"
                    />
                  )
                )}
              </div>
              <div className="d-flex flex-column flex-grow-1">
                {[
                  "Reason for Calling",
                  "Calling on Behalf of",
                  "Where did you hear about us",
                  "Business Name",
                ].map((label) => (
                  <Form.Check
                    key={label}
                    type="checkbox"
                    label={label}
                    name={label.toLowerCase().replace(/\s/g, "")}
                    checked={
                      selectedOptions[label.toLowerCase().replace(/\s/g, "")]
                    }
                    onChange={handleCheckboxChange}
                    className="purple-custom-checkbox"
                  />
                ))}
              </div>
            </div>
          </Card>
          {/* Call Handling Options */}
          <Card className="p-4 mb-3 border">
            <h6>Call Handling Options</h6>
            <p className="text-muted">What should we do next?</p>

            {/* Take a Message */}
            <Row className="align-items-center border rounded-3 p-3 mb-2">
              <Col xs="auto">
                <img
                  src="/assets/message-square.svg"
                  alt="Message"
                  className="p-2 bg-icon"
                />
              </Col>
              <Col className="px-0">
                <label className="fw-bold">Take a Message</label>
              </Col>
              <Col xs="auto fs-5">
                <Form.Check
                  type="switch"
                  checked={messageEnabled}
                  onChange={handleMessageToggle}
                  className={messageEnabled ? "toggle-btn" : ""}
                />
              </Col>
            </Row>

            {/* Schedule Appointment */}
            <Row className="align-items-center border rounded-3 p-3">
              <Col xs="auto">
                <img
                  src="/assets/calendar.svg"
                  alt="Calendar"
                  className="p-2 bg-icon"
                />
              </Col>
              <Col className="px-0">
                <label className="fw-bold">Schedule Appointment</label>
              </Col>
              <Col xs="auto fs-5">
                <Form.Check
                  type="switch"
                  checked={scheduleEnabled}
                  onChange={handleScheduleToggle}
                  className={scheduleEnabled ? "toggle-btn" : ""}
                />
              </Col>
            </Row>

            {scheduleEnabled && (
              <Row>
                <Form.Group className="mt-2 col-md-6 position-relative">
                  <Form.Label>Select Option</Form.Label>
                  <Form.Control
                    as="select"
                    value={appointmentType}
                    onChange={(e) => setAppointmentType(e.target.value)}
                  >
                    <option value="">Select an option</option>
                    <option value="Cal.co">Cal.com</option>
                    <option value="Zapier">Zapier (Powered by Nouraa)</option>
                  </Form.Control>
                  <img
                    src="/assets/chevron-down.svg"
                    alt="Icon"
                    className="position-absolute dropdown-icon"
                  />
                </Form.Group>

                {appointmentType === "Cal.co" && (
                  <>
                    <Form.Group className="col-md-6 mt-2">
                      <Form.Label>Event Id</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter here"
                        value={additionalInfo}
                        onChange={(e) => setAdditionalInfo(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="col-md-12 mt-3">
                      <Form.Label>Cal.com API Key</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter API Key"
                        value={calcomApiKey}
                        onChange={(e) => setCalcomApiKey(e.target.value)}
                      />
                    </Form.Group>
                    <p className="text-muted mt-3">
                      If you don't have a Cal.com account, you can create one{" "}
                      <Link
                        to="https://cal.com/"
                        target="_blank"
                        className="purple-color fw-bold text-decoration-none"
                      >
                        here.
                      </Link>
                      Please make sure to provide the correct details. If
                      details are incorrect then this will result in no
                      appointments scheduled.
                    </p>
                  </>
                )}
              </Row>
            )}
          </Card>
          {/* Agent Instruction */}
          <Card className="p-4 mb-3  border">
            <h6>Agent Instruction (Optional)</h6>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter the instruction..."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          </Card>
          <div className="border rounded p-4 bg-white shadow-sm w-100">
            <h6 className="text-dark fw-semibold mb-2">
              Behavioural instruction for the Agent (Optional)
            </h6>

            {/* Tags */}
            <div className="btn-tags-container d-flex flex-wrap gap-3 py-3">
              {behaviorTags.map((tag) => (
                <button
                  key={tag}
                  className={`btn fs-6 fw-bold btn-sm px-3 py-1 rounded-pill ${
                    selectedTags.includes(tag)
                      ? "bg-light purple-color"
                      : "bg-body-secondary gray-color"
                  }`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>

            {/* Checkbox */}
            <div className="form-check mt-3 purple-custom-checkbox">
              <input
                type="checkbox"
                className="form-check-input"
                checked={addFillers}
                onChange={() => setAddFillers(!addFillers)}
              />
              <label className="form-check-label gray-color fs-one">
                Add Natural Fillers like ‘uhm’, ‘hmm’, or ‘ahaan’.
              </label>
            </div>
            <Button
              className="d-none"
              onClick={() => custHandleSaveNoura}
            ></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedSetupCustNoura;
